// src/Dashboard/pages/user/FicheAdherent/components/AdherentSelector.jsx

import React, { useMemo, useState } from "react";
import { FaCalendarAlt, FaSearch } from "react-icons/fa";

import {
  formatAdherentName,
  getAdherentPhotoUrl,
  getStatusPillClass,
  normalizeSearch,
} from "../utils/ficheAdherentDashboardUtils";

export default function AdherentSelector({
  adherents = [],
  selectedAdherentId,
  onSelect,
  loading = false,
}) {
  const [search, setSearch] = useState("");

  const filteredAdherents = useMemo(() => {
    const q = normalizeSearch(search);

    if (!q) return adherents;

    return adherents.filter((adherent) => {
      const label = normalizeSearch(
        `${adherent.nom || ""} ${adherent.prenom || ""} ${
          adherent.email || ""
        } ${adherent.licence_number || ""}`
      );

      return label.includes(q);
    });
  }, [adherents, search]);

  if (loading) {
    return (
      <div className="rounded-[26px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950">
        <div className="mb-4 h-10 w-full animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800" />
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 3 }).map((_, index) => (
            <div
              key={index}
              className="h-24 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
            />
          ))}
        </div>
      </div>
    );
  }

  if (!adherents.length) {
    return (
      <div className="rounded-[26px] border border-dashed border-slate-300 bg-slate-50 p-6 text-center dark:border-slate-700 dark:bg-slate-900/60">
        <p className="font-bold text-slate-900 dark:text-white">
          Aucun adhérent trouvé
        </p>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Aucun adhérent n’est actuellement rattaché à votre compte.
        </p>
      </div>
    );
  }

  return (
    <div className="rounded-[26px] border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-800 dark:bg-slate-950">
      <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h2 className="text-lg font-bold text-slate-900 dark:text-white">
            Choisir un adhérent
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Sélectionnez une fiche, puis consultez les informations par onglet.
          </p>
        </div>

        <div className="relative w-full lg:max-w-sm">
          <FaSearch className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-slate-400" />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Rechercher un nom, email, licence..."
            className="w-full rounded-2xl border border-slate-200 bg-slate-50 py-3 pl-10 pr-3 text-sm font-semibold text-slate-800 outline-none transition focus:border-violet-300 focus:bg-white focus:ring-4 focus:ring-violet-100 dark:border-slate-800 dark:bg-slate-900 dark:text-slate-100 dark:focus:border-violet-700 dark:focus:ring-violet-900/30"
          />
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {filteredAdherents.map((adherent) => {
          const active = String(adherent.id) === String(selectedAdherentId);

          return (
            <button
              key={adherent.id}
              type="button"
              onClick={() => onSelect?.(adherent.id)}
              className={[
                "group flex w-full items-center gap-3 rounded-2xl border p-3 text-left transition",
                active
                  ? "border-violet-300 bg-violet-50 shadow-sm ring-4 ring-violet-100 dark:border-violet-700 dark:bg-violet-900/20 dark:ring-violet-900/30"
                  : "border-slate-200 bg-white hover:border-violet-200 hover:bg-slate-50 dark:border-slate-800 dark:bg-slate-950 dark:hover:border-violet-800 dark:hover:bg-slate-900",
              ].join(" ")}
            >
              <img
                src={getAdherentPhotoUrl(adherent)}
                alt={formatAdherentName(adherent)}
                className="h-14 w-14 shrink-0 rounded-2xl object-cover ring-1 ring-slate-200 dark:ring-slate-700"
                onError={(event) => {
                  event.currentTarget.onerror = null;
                  event.currentTarget.src = "/img/user_avatar.jpg";
                }}
              />

              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-bold text-slate-900 dark:text-white">
                  {formatAdherentName(adherent)}
                </p>

                <div className="mt-1 flex flex-wrap items-center gap-1.5">
                  <span
                    className={[
                      "inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold ring-1",
                      getStatusPillClass(adherent.statut_inscription),
                    ].join(" ")}
                  >
                    {adherent.statut_inscription || "Statut inconnu"}
                  </span>

                  {adherent.saison_active && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[10px] font-bold text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700">
                      <FaCalendarAlt />
                      {adherent.saison_active}
                    </span>
                  )}
                </div>
              </div>
            </button>
          );
        })}
      </div>

      {!filteredAdherents.length && (
        <div className="mt-4 rounded-2xl bg-slate-50 p-4 text-sm text-slate-500 dark:bg-slate-900 dark:text-slate-400">
          Aucun adhérent ne correspond à cette recherche.
        </div>
      )}
    </div>
  );
}